///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Video scene export (development builds): the clapperboard beside Share.
/// Frame the shot, turn on layers, open a cutout, scrub the clock to the
/// clip's end, then copy or download the RenderScene JSON
/// (utils/renderScene.ts) for the video renderer - the same export as
/// __ebDebug.exportRenderScene, without the console. Format / style / span
/// are the options that change the picture; everything else the renderer
/// templates per subject kind.
///

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useAppSelector } from "@/redux/store"
import { exportRenderScene, type ExportRenderSceneOptions } from "@/utils/renderScene"
import { cn } from "@/utils/utils"
import type { RenderScene } from "@shared-types"
import { CheckIcon, Clapperboard, CopyIcon, DownloadIcon } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

const cCopiedFeedbackMs = 1500
const cDevEmbedUrl = `${window.location.origin}/embed.html`

type Format = NonNullable<ExportRenderSceneOptions["format"]>
type Style = NonNullable<ExportRenderSceneOptions["style"]>

const cFormats: Array<{ id: Format; label: string }> = [
	{ id: "reel", label: "Reel 9:16" },
	{ id: "landscape", label: "Landscape 16:9" },
]
const cStyles: Array<{ id: Style; label: string }> = [
	{ id: "clean", label: "Clean clouds" },
	{ id: "ir", label: "Enhanced IR" },
]
const cSpans = [3, 6, 12, 24]

function PillGroup<T extends string | number>({ label, options, value, onChange }: {
	label: string
	options: Array<{ id: T; label: string }>
	value: T
	onChange: (id: T) => void
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-[10px] uppercase tracking-wide text-white/40">{label}</span>
			<div className="flex items-center gap-1" role="radiogroup" aria-label={label}>
				{options.map(option => {
					const selected = option.id === value
					return (
						<button
							key={String(option.id)}
							type="button"
							role="radio"
							aria-checked={selected}
							onClick={() => onChange(option.id)}
							className={cn(
								"flex-1 h-7 px-2 text-[11px] rounded-full border transition-colors duration-200",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
								selected
									? "bg-violet-500/20 text-violet-200 border-violet-500/60"
									: "bg-white/5 text-white/50 border-transparent hover:text-white hover:bg-white/10"
							)}
						>
							{option.label}
						</button>
					)
				})}
			</div>
		</div>
	)
}

/** What the export will be about, read live so the summary tracks focus/cutout changes */
function sceneSummary(scene: RenderScene): string {
	const subject = scene.subject.name ?? (scene.subject.kind === "coords" ? "free view" : `${scene.subject.kind} ${scene.subject.id ?? ""}`)
	const parts = [subject]
	if (scene.cutout) parts.push(`${scene.cutout.kind} cutout`)
	parts.push(`${scene.layers.length} layer${scene.layers.length === 1 ? "" : "s"}`)
	return parts.join(" · ")
}

function sceneFilename(scene: RenderScene): string {
	const id = (scene.subject.id ?? `${scene.camera.lookAt.lat.toFixed(1)}_${scene.camera.lookAt.lon.toFixed(1)}`).replace(/[^\w.-]+/g, "_")
	const stamp = new Date(scene.clock.end).toISOString().slice(0, 16).replace(/[-:]/g, "").replace("T", "-")
	return `scene-${scene.subject.kind}-${id}-${stamp}.json`
}

export function RenderSceneMenu({ triggerClassName }: { triggerClassName: string }) {
	const [open, setOpen] = useState(false)
	const [format, setFormat] = useState<Format>("reel")
	const [style, setStyle] = useState<Style>("clean")
	const [spanHours, setSpanHours] = useState(12)
	const [devEmbed, setDevEmbed] = useState(true)
	const [copied, setCopied] = useState(false)
	const copiedTimer = useRef<number | null>(null)

	// Re-render the summary when the inputs the export reads change
	const target = useAppSelector(state => state.userInterface.infoWindow.title)
	const cutout = useAppSelector(state => state.dialog.quakeCutout?.quakeId ?? state.dialog.stormCutout?.stormId)
	const layerCount = useAppSelector(state => state.earthbrowser.visibleDatasets.length)

	useEffect(() => () => {
		if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
	}, [])

	const options: ExportRenderSceneOptions = { format, style, spanHours, appUrl: devEmbed ? cDevEmbedUrl : undefined }
	// Summary preview; cheap (reads the store, no I/O). The store selectors
	// above are deps so it follows focus, cutout and layer changes
	const preview = useMemo(
		() => open ? exportRenderScene(options) : null,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[open, format, style, spanHours, devEmbed, target, cutout, layerCount]
	)

	const download = (scene: RenderScene) => {
		const blob = new Blob([JSON.stringify(scene, null, 2)], { type: "application/json" })
		const url = URL.createObjectURL(blob)
		const anchor = document.createElement("a")
		anchor.href = url
		anchor.download = sceneFilename(scene)
		anchor.click()
		window.setTimeout(() => URL.revokeObjectURL(url), 1000)
	}

	const handleCopy = async () => {
		const scene = exportRenderScene(options)
		try {
			await navigator.clipboard.writeText(JSON.stringify(scene, null, 2))
		} catch {
			// Clipboard denied (unfocused document, permissions): hand the
			// file over instead so the export never silently fails
			download(scene)
			return
		}
		setCopied(true)
		if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
		copiedTimer.current = window.setTimeout(() => setCopied(false), cCopiedFeedbackMs)
	}

	const handleDownload = () => download(exportRenderScene(options))

	const actionClass = cn(
		"flex-1 flex items-center justify-center gap-1.5 h-8 px-3 text-xs rounded-lg border border-white/15",
		"bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-colors duration-200",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
	)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className={cn(triggerClassName, open && "bg-white/10 text-white")}
					title="Export a video scene (dev)"
					aria-label="Export video scene"
					aria-expanded={open}
				>
					<Clapperboard className="w-4 h-4" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				sideOffset={8}
				collisionPadding={8}
				className={cn(
					"z-[1100] w-[300px] flex flex-col gap-3 p-3 text-white",
					"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl"
				)}
				data-render-scene-menu
			>
				<div className="flex flex-col gap-0.5">
					<span className="text-sm font-semibold">Video scene</span>
					<span className="text-[11px] text-white/60 truncate" title={preview ? sceneSummary(preview) : undefined}>
						{preview ? sceneSummary(preview) : ""}
					</span>
					<span className="text-[11px] text-white/40">
						{preview ? `Clip ends ${new Date(preview.clock.end).toUTCString().slice(5, 22)} UTC · ${spanHours} h back` : ""}
					</span>
				</div>

				<PillGroup label="Format" options={cFormats} value={format} onChange={setFormat} />
				<PillGroup label="Clouds" options={cStyles} value={style} onChange={setStyle} />
				<PillGroup label="Span" options={cSpans.map(h => ({ id: h, label: `${h} h` }))} value={spanHours} onChange={setSpanHours} />

				<label className="flex items-center gap-2 text-[11px] text-white/70 cursor-pointer select-none">
					<input
						type="checkbox"
						checked={devEmbed}
						onChange={e => setDevEmbed(e.target.checked)}
						className="accent-violet-500"
					/>
					Capture this dev server instead of production
				</label>

				<div className="flex items-center gap-2">
					<button type="button" onClick={() => { void handleCopy() }} className={cn(actionClass, copied && "text-green-300 border-green-400/40")}>
						{copied ? <CheckIcon className="w-3.5 h-3.5" /> : <CopyIcon className="w-3.5 h-3.5" />}
						{copied ? "Copied" : "Copy JSON"}
					</button>
					<button type="button" onClick={handleDownload} className={actionClass}>
						<DownloadIcon className="w-3.5 h-3.5" />
						Download
					</button>
				</div>
				<span className="text-[10px] text-white/40 leading-snug">
					Render with <code className="text-white/60">bun run once render-video --scene file.json</code> in weather-services.
				</span>
			</PopoverContent>
		</Popover>
	)
}
